import { useState, useEffect, useRef } from 'react';
import { Search, X, Calendar } from 'lucide-react';
import type {
    DocuWareInvoiceFilters, DocuWareFacets, DocuWareCompanySummary, DocuWarePostingStatus,
} from '../../types/docuwareInvoices';
import { companyLabel } from '../../types/docuwareInvoices';

/**
 * Filter bar for /docuware-invoices.
 *
 * Controlled and stateless apart from the search draft: the page owns the filter object and
 * this patches it upward. Organisation-level filter first, per house convention.
 *
 * Option lists come from the server (`facets`), never from a constant here. The two DocuWare
 * organisations file invoices under different vocabularies — dbi uses Waren / Spedition /
 * Allgemein, dch uses Warenrechnung / Sonstige Rechnung — so a hard-coded list would offer
 * values that match nothing. It also means each control can hide itself when its list is
 * empty, which is how "Workflow status" disappears for dch: dch's DocuWare search dialog
 * does not project that field at all. No company is named anywhere in this file.
 *
 * There is deliberately NO due-date filter. FALLIGKEITSDATUM is filled on only 37% of dbi
 * and 8% of dch documents, so filtering on it would silently hide most of the table and look
 * like an empty backlog.
 */

interface Props {
    filters: DocuWareInvoiceFilters;
    facets: DocuWareFacets;
    companies: DocuWareCompanySummary[];
    activeFilterCount: number;
    onChange: (next: DocuWareInvoiceFilters) => void;
}

const POSTING_STATUS: { value: DocuWarePostingStatus; label: string; title: string }[] = [
    { value: 'all', label: 'All', title: 'Every document, posted or not' },
    { value: 'open', label: 'Not posted', title: 'No Business Central posting found yet' },
    { value: 'posted', label: 'Posted', title: 'Matched to a Business Central posting' },
];

/**
 * Every control gets a label, including the native ones: without it a select and a text
 * input in the same grid row sit 17px apart vertically.
 */
function Field({ label, children, className = '' }: {
    label: string; children: React.ReactNode; className?: string;
}) {
    return (
        <div className={className}>
            <label className="mb-1 block text-xs font-medium text-gray-600">{label}</label>
            {children}
        </div>
    );
}

const INPUT = 'h-8 w-full rounded border border-gray-300 px-2 text-sm text-gray-900 ' +
    'focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500';

function FacetSelect({ label, value, options, placeholder, onChange }: {
    label: string;
    value: string | undefined;
    options: { value: string; documents: number }[];
    placeholder: string;
    onChange: (v: string) => void;
}) {
    // An empty facet means the field does not exist for this company. Rendering a select
    // with no options would suggest the data is missing rather than not applicable.
    if (options.length === 0) return null;
    return (
        <Field label={label}>
            <select className={INPUT} value={value ?? ''} onChange={(e) => onChange(e.target.value)}>
                <option value="">{placeholder}</option>
                {options.map((o) => (
                    <option key={o.value} value={o.value}>
                        {o.value} ({o.documents.toLocaleString('de-CH')})
                    </option>
                ))}
            </select>
        </Field>
    );
}

export function DocuWareInvoiceFiltersBar({
    filters, facets, companies, activeFilterCount, onChange,
}: Props) {
    const set = (patch: Partial<DocuWareInvoiceFilters>) => onChange({ ...filters, ...patch });

    // One keystroke must not fire one request. The draft is local; only the debounced value
    // reaches the filter object.
    const [searchDraft, setSearchDraft] = useState(filters.search ?? '');
    const committed = useRef(filters.search ?? '');

    useEffect(() => {
        // Keep in step when the filters are cleared from outside (the Clear button).
        if (filters.search !== committed.current) {
            committed.current = filters.search ?? '';
            setSearchDraft(filters.search ?? '');
        }
    }, [filters.search]);

    useEffect(() => {
        const trimmed = searchDraft.trim();
        if (trimmed === committed.current) return;
        const t = setTimeout(() => {
            committed.current = trimmed;
            onChange({ ...filters, search: trimmed || undefined });
        }, 300);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchDraft]);

    const status = filters.postingStatus ?? 'all';

    return (
        <div className="space-y-3 rounded-lg border border-gray-200 bg-white p-3">
            {/* Row 1 — organisation first, then the question the page exists to answer. */}
            <div className="flex flex-wrap items-end gap-3">
                {companies.length > 1 && (
                    <Field label="Company" className="w-56">
                        <select
                            className={INPUT}
                            value={filters.companyCode ?? ''}
                            onChange={(e) => set({ companyCode: e.target.value || undefined })}
                        >
                            <option value="">All companies</option>
                            {companies.map((c) => (
                                <option key={c.companyCode} value={c.companyCode}>
                                    {companyLabel(c.companyCode)} ({c.documents.toLocaleString('de-CH')})
                                </option>
                            ))}
                        </select>
                    </Field>
                )}

                <Field label="Posting status">
                    <div className="flex h-8 items-center gap-1">
                        {POSTING_STATUS.map((s) => (
                            <button
                                key={s.value}
                                type="button"
                                title={s.title}
                                aria-pressed={status === s.value}
                                onClick={() => set({ postingStatus: s.value })}
                                className={`h-8 rounded border px-2.5 text-xs font-medium transition-colors ${
                                    status === s.value
                                        ? 'border-blue-500 bg-blue-50 text-blue-700'
                                        : 'border-gray-300 bg-white text-gray-600 hover:bg-gray-50'
                                }`}
                            >
                                {s.label}
                            </button>
                        ))}
                    </div>
                </Field>

                <Field label="Search" className="w-80">
                    <div className="relative">
                        <Search className="pointer-events-none absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                        <input
                            type="text"
                            className={`${INPUT} pl-8`}
                            placeholder="Supplier, invoice no., document ID or BC document"
                            value={searchDraft}
                            onChange={(e) => setSearchDraft(e.target.value)}
                        />
                    </div>
                </Field>

                {activeFilterCount > 0 && (
                    <button
                        type="button"
                        onClick={() => onChange({})}
                        className="ml-auto inline-flex h-8 items-center gap-1 rounded border border-gray-300 px-2.5 text-xs font-medium text-gray-600 hover:bg-gray-50"
                    >
                        <X className="h-3.5 w-3.5" />
                        Clear filters ({activeFilterCount})
                    </button>
                )}
            </div>

            {/* Row 2 — the two dates. Stored is the real arrival; invoice date is the document's own. */}
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-4">
                <Field label="Stored in DocuWare from">
                    <DateInput value={filters.storedFrom} onChange={(v) => set({ storedFrom: v })} />
                </Field>
                <Field label="Stored in DocuWare to">
                    <DateInput value={filters.storedTo} onChange={(v) => set({ storedTo: v })} />
                </Field>
                <Field label="Invoice date from">
                    <DateInput value={filters.invoiceDateFrom} onChange={(v) => set({ invoiceDateFrom: v })} />
                </Field>
                <Field label="Invoice date to">
                    <DateInput value={filters.invoiceDateTo} onChange={(v) => set({ invoiceDateTo: v })} />
                </Field>
            </div>

            {/* Row 3 — document attributes and amount. */}
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-5">
                <FacetSelect
                    label="Document type" placeholder="Any type"
                    value={filters.documentType} options={facets.documentType}
                    onChange={(v) => set({ documentType: v || undefined })}
                />
                <FacetSelect
                    label="Invoice category" placeholder="Any category"
                    value={filters.invoiceCategory} options={facets.invoiceCategory}
                    onChange={(v) => set({ invoiceCategory: v || undefined })}
                />
                <FacetSelect
                    label="Workflow status" placeholder="Any status"
                    value={filters.workflowStatus} options={facets.workflowStatus}
                    onChange={(v) => set({ workflowStatus: v || undefined })}
                />
                <FacetSelect
                    label="Currency" placeholder="Any currency"
                    value={filters.currency} options={facets.currency}
                    onChange={(v) => set({ currency: v || undefined })}
                />
                {/* Amounts are not converted — pair this with the currency filter to compare
                    like with like. */}
                <Field label="Amount from / to">
                    <div className="flex items-center gap-1">
                        <input
                            type="number" inputMode="decimal" placeholder="min"
                            className={`${INPUT} tabular-nums`}
                            value={filters.amountMin ?? ''}
                            onChange={(e) => set({ amountMin: e.target.value || undefined })}
                        />
                        <input
                            type="number" inputMode="decimal" placeholder="max"
                            className={`${INPUT} tabular-nums`}
                            value={filters.amountMax ?? ''}
                            onChange={(e) => set({ amountMax: e.target.value || undefined })}
                        />
                    </div>
                </Field>
            </div>
        </div>
    );
}

function DateInput({ value, onChange }: { value: string | undefined; onChange: (v: string | undefined) => void }) {
    return (
        <div className="relative">
            <Calendar className="pointer-events-none absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <input
                type="date"
                className={`${INPUT} pl-8`}
                value={value ?? ''}
                onChange={(e) => onChange(e.target.value || undefined)}
            />
        </div>
    );
}
